import { ChangeDetectorRef, Inject, Injectable } from '@angular/core';
import { DateAdapter } from '@angular/material/core';
import { BcitiLang } from '@bciti/types';
import { Actions, Effect, ROOT_EFFECTS_INIT, createEffect, ofType } from '@ngrx/effects';
import { Store } from '@ngrx/store';
import { TranslateService } from '@ngx-translate/core';
import { combineLatest, iif, of } from 'rxjs';
import { catchError, distinctUntilChanged, filter, map, mergeMap, switchMap, take, tap } from 'rxjs/operators';
import { SetOrgTagAction } from 'src/app/auth/store/auth.actions';
import { authOrgSelector, isAuthenticatedSelector } from 'src/app/auth/store/auth.selectors';
import { State } from 'src/app/store';
import { UpdateProfileAction } from 'src/app/user/store/user.actions';
import { profileSelector } from 'src/app/user/store/user.selectors';
import { UpdateUserRecordPayload } from 'src/app/user/user.model';
import { DEFAULT_LANG } from 'src/config/lang.config';
import { MessageService } from '../services/message/message.abstract.service';
import { OrganisationService } from '../services/organisation/organisation.abstract.service';
import { ServiceStateService } from '../services/service-state/service-state.abstract.service';
import { ServiceStatusAdminResponse } from '../services/service-state/service-state.model';
import {
    CreateServiceAction,
    CreateServiceCategoryAction,
    CreateServiceCategoryErrorAction,
    CreateServiceCategorySuccessAction,
    CreateServiceErrorAction,
    CreateServiceStatusAction,
    CreateServiceStatusErrorAction,
    CreateServiceStatusSuccessAction,
    CreateServiceSuccessAction,
    DeleteServiceAction,
    DeleteServiceCategoryAction,
    DeleteServiceCategoryErrorAction,
    DeleteServiceCategorySuccessAction,
    DeleteServiceErrorAction,
    DeleteServiceStatusAction,
    DeleteServiceStatusErrorAction,
    DeleteServiceStatusSuccessAction,
    DeleteServiceSuccessAction,
    DuplicateServiceAction,
    DuplicateServiceErrorAction,
    DuplicateServiceSuccessAction,
    GetNavigationAction,
    GetOrganisationConfigurationAction,
    GetOrganisationErrorAction,
    GetOrganisationFeaturesConfigurationAction,
    GetOrganisationsAction,
    GetOrganisationsErrorAction,
    GetOrganisationsSuccessAction,
    GetServiceCategoriesAction,
    GetServiceCategoriesErrorAction,
    GetServiceCategoriesSuccessAction,
    GetServiceStatusesAction,
    GetServiceStatusesErrorAction,
    GetServiceStatusesSuccessAction,
    GetServicesAction,
    GetServicesErrorAction,
    GetServicesSuccessAction,
    InitLanguageAction,
    RefreshOrganisationAction,
    ResetLanguageAction,
    SaveLanguageAction,
    SetIsGenericUrlAction,
    SetLanguageAction,
    SetNavigationAction,
    SetOrganisationAction,
    SetOrganisationConfigurationAction,
    SetOrganisationFeaturesConfigurationAction,
    SetOrganisationsAction,
    SetServiceCategoriesAction,
    SetServiceStatusesAction,
    SetServicesAction,
    UpdateServiceAction,
    UpdateServiceCategoryAction,
    UpdateServiceCategoryErrorAction,
    UpdateServiceCategorySuccessAction,
    UpdateServiceErrorAction,
    UpdateServiceOrderAction,
    UpdateServiceOrderErrorAction,
    UpdateServiceOrderSuccessAction,
    UpdateServiceStatusAction,
    UpdateServiceStatusErrorAction,
    UpdateServiceStatusSuccessAction,
    UpdateServiceSuccessAction,
    GetCollectionsAction,
    GetCollectionsSuccessAction,
    SetCollectionsAction,
    GetCollectionsErrorAction,
    DeleteCollectionAction,
    DeleteCollectionSuccessAction,
    DeleteCollectionErrorAction,
    GetOrganisationAreasAction,
    GetOrganisationAreasSuccessAction,
    SetOrganisationAreasAction,
    GetOrganisationAreasErrorAction,
    UpdateOrganisationAreaAction,
    UpdateOrganisationAreaSuccessAction,
    UpdateOrganisationAreaErrorAction,
    DeleteOrganisationAreaAction,
    DeleteOrganisationAreaSuccessAction,
    DeleteOrganisationAreaErrorAction,
    CreateOrganisationAreaAction,
    CreateOrganisationAreaSuccessAction,
    CreateOrganisationAreaErrorAction,
    ClearOrganisationAreasAction,
    DuplicateCollectionAction,
    DuplicateCollectionSuccessAction,
    DuplicateCollectionErrorAction,
    CreateCollectionErrorAction,
    CreateCollectionSuccessAction,
    CreateCollectionAction,
    UpdateCollectionAction,
    UpdateCollectionSuccessAction,
    UpdateCollectionErrorAction,
    GetOrganisationColourAction,
    SetOrganisationColourAction,
    SetOrganisationColourSuccessAction,
    SetOrganisationColourFailureAction,
    GetOrganisationColourSuccessAction,
    GetOrganisationColourFailureAction,
    DeleteAccessErrorAction,
    DeleteAccessSuccessAction,
    SetAccessAction,
    DeleteAccessAction,
    UpdateAccessErrorAction,
    UpdateAccessAction,
    UpdateAccessSuccessAction,
    CreateAccessErrorAction,
    CreateAccessSuccessAction,
    CreateAccessAction,
    GetAccessAction,
    GetAccessSuccessAction,
    GetAccessErrorAction,
    BatchAssignRoleSuccessAction,
    BatchAssignRoleAction,
    AssignRoleAction,
    AssignRoleSuccessAction,
    AssignRoleErrorAction,
    BatchAssignRoleErrorAction,
} from './global.actions';
import {
    languageSelector,
    organisationConfigurationSelector,
    organisationTagSelector,
    serviceCategoriesSelector,
    serviceStatusesSelector,
    servicesSelector,
    collectionsSelector,
    areasSelector,
    accessSelector,
} from './global.selectors';
import { CollectionsService } from '../services/collections/collections.abstract.service';
import { AccessControlService } from '../services/access-control/access-control.abstract.service';

@Injectable()
export class GlobalEffects {
    constructor(
        private store: Store<State>,
        private actions$: Actions,
        private serviceStateService: ServiceStateService,
        private organisationService: OrganisationService,
        private translateService: TranslateService,
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        private dateAdapter: DateAdapter<any>,
        @Inject('Window') private window: Window,
        private messageService: MessageService,
        private collectionsService: CollectionsService,
        private accessService: AccessControlService,
    ) {}

    detectLang$ = createEffect(() =>
        this.actions$.pipe(
            ofType(ResetLanguageAction),
            mergeMap(() =>
                combineLatest([this.store.select(profileSelector), this.store.select(organisationConfigurationSelector), this.store.select(languageSelector)]).pipe(
                    filter(([profile, orgConfig, storeLang]) => !!profile && !!orgConfig?.lang?.available && !!orgConfig?.lang?.default && !!storeLang),
                    take(1),
                    map(([profile, config, storeLang]) => {
                        const actions = [];
                        const preferredLanguage = profile?.preferences?.language;
                        if (config.lang.available.includes(preferredLanguage) && preferredLanguage !== storeLang) {
                            // preference available (and different than storeLang)
                            actions.push(SetLanguageAction({ language: preferredLanguage }));
                        } else if (!config.lang.available.includes(preferredLanguage) && !config.lang.available.includes(storeLang)) {
                            // preference is not available and storeLang is not available
                            actions.push(SetLanguageAction({ language: config.lang.default }));
                        }
                        return actions;
                    }),
                    switchMap((actions) => actions),
                ),
            ),
        ),
    );

    initLang$ = createEffect(() =>
        this.actions$.pipe(
            take(1),
            ofType(ROOT_EFFECTS_INIT),
            map(() => InitLanguageAction()),
        ),
    );

    initLangEffect$ = createEffect(() =>
        this.actions$.pipe(
            ofType(InitLanguageAction),
            map(() => {
                const localStorageLanguage = this.window.localStorage.getItem('bciti-language');
                if (localStorageLanguage === 'en' || localStorageLanguage === 'fr') {
                    return SetLanguageAction({ language: localStorageLanguage });
                }
                // browser lang ex: 'en-US'
                const browserLanguage = navigator.language.substring(0, 2).toLowerCase();
                switch (browserLanguage) {
                    case 'en':
                    case 'fr':
                        return SetLanguageAction({ language: browserLanguage });
                    default:
                        console.error(`unsupported device language '${browserLanguage}', using default language '${DEFAULT_LANG}'`);
                        return SetLanguageAction({ language: DEFAULT_LANG });
                }
            }),
        ),
    );

    setLanguage$ = createEffect(() =>
        this.actions$.pipe(
            // tslint:disable-next-line: max-line-length
            ofType(SetLanguageAction),
            tap(({ language }) => {
                localStorage.setItem('bciti-language', language);
                // keep <html lang> in sync so browsers don't offer to translate the page
                document.documentElement.lang = language;
            }),
            filter(({ language }) => this.translateService.currentLang !== language && Object.values(BcitiLang).includes(language as BcitiLang)),
            switchMap(({ language }) => {
                this.dateAdapter.setLocale(language);
                this.translateService.use(language);
                return [];
            }),
        ),
    );

    saveLanguage$ = createEffect(() =>
        this.actions$.pipe(
            ofType(SaveLanguageAction),
            mergeMap(({ language }) => {
                const payload: UpdateUserRecordPayload = {
                    preferences: {
                        language,
                    },
                };
                return [UpdateProfileAction({ payload })];
            }),
        ),
    );

    // TODO Figure out a better way to tell
    @Effect()
    initOrg$ = this.actions$.pipe(
        ofType(ROOT_EFFECTS_INIT, SetOrgTagAction),
        mergeMap(() => combineLatest([of(location.hostname.split('.')), this.store.select(profileSelector)])),
        mergeMap(([parts, profile]) => {
            const hasSubdomain = parts.length > 2 && parts[0].indexOf('-lite') === -1 && parts[0].indexOf('plus') === -1 && location.port !== '4200';

            return combineLatest([
                iif(
                    () => hasSubdomain,
                    this.organisationService.getSubdomain().pipe(switchMap((sd) => this.organisationService.getOrganisationBySubdomain(sd))),
                    this.organisationService.getOrganisationFromAuth(),
                ),
                of(profile),
            ]);
        }),
        map(([organisation]) => {
            const actions: any[] = [];

            if (organisation) {
                actions.push(
                    SetOrganisationAction({
                        organisation: {
                            _id: organisation._id,
                            id: organisation.id,
                            name: organisation.name,
                            tag: organisation.tag,
                            logo: organisation.logo,
                            subdomain: organisation.subdomain,
                            bounds: organisation.bounds,
                            subscriptions: organisation.subscriptions,
                            taxes: organisation.taxes,
                            mapZoom: organisation.mapZoom,
                            timeZone: organisation.timeZone,
                        },
                    }),
                );
                actions.push(SetIsGenericUrlAction({ isGenericUrl: false }));
                return actions;
            }
        }),
        switchMap((actions) => actions),
        catchError((_error) => of(GetOrganisationsAction())),
    );

    @Effect()
    getOrgConfigSuccess$ = this.actions$.pipe(
        ofType(SetOrgTagAction, ROOT_EFFECTS_INIT),
        mergeMap(() => this.store.select(isAuthenticatedSelector)),
        map((isLogged) => {
            const actions: any[] = [];
            if (isLogged) {
                actions.push(GetOrganisationConfigurationAction());
            }
            return actions;
        }),
        switchMap((actions) => actions),
    );

    @Effect()
    getOrgFeaturesConfigSuccess$ = this.actions$.pipe(
        ofType(SetOrgTagAction, ROOT_EFFECTS_INIT),
        mergeMap(() => this.store.select(isAuthenticatedSelector)),
        map((isLogged) => {
            const actions: any[] = [];
            if (isLogged) {
                actions.push(GetOrganisationFeaturesConfigurationAction());
            }
            return actions;
        }),
        switchMap((actions) => actions),
    );

    initPlatform$ = createEffect(() =>
        this.actions$.pipe(
            ofType(ROOT_EFFECTS_INIT),
            mergeMap(() => combineLatest([this.store.select(authOrgSelector).pipe(distinctUntilChanged()), this.store.select(isAuthenticatedSelector)]).pipe(filter(([tag, auth]) => !!tag && !!auth))),
            switchMap(() => [GetOrganisationConfigurationAction(), ResetLanguageAction(), GetOrganisationFeaturesConfigurationAction()]),
        ),
    );

    @Effect()
    getOrgs$ = this.actions$.pipe(
        ofType(GetOrganisationsAction),
        mergeMap(() => this.organisationService.getOrganisations()),
        map((organisations) => [GetOrganisationsSuccessAction({ organisations }), SetOrganisationsAction({ organisations })]),
        switchMap((actions) => actions),
        catchError((error) => of(GetOrganisationsErrorAction({ error }))),
    );

    @Effect()
    refreshOrg$ = this.actions$.pipe(
        ofType(RefreshOrganisationAction),
        mergeMap(() => this.store.select(organisationTagSelector)),
        filter((tag) => !!tag),
        mergeMap((tag) => this.organisationService.getOrganisationByTag(tag)),
        map((organisation) => [SetOrganisationAction({ organisation })]),
        switchMap((actions) => actions),
        catchError((error) => of(GetOrganisationErrorAction({ error }))),
    );

    @Effect()
    getNavigation$ = this.actions$.pipe(
        ofType(GetNavigationAction),
        switchMap(() =>
            combineLatest([this.store.select(organisationTagSelector), this.store.select(authOrgSelector)]).pipe(
                map(([orgTag, authTag]) => ({ orgTag, authTag, resolvedTag: orgTag || authTag })),
                filter(({ resolvedTag }) => !!resolvedTag),
                take(1),
                mergeMap(({ resolvedTag }) => this.organisationService.getOrganisationNavigation(resolvedTag)),
                map((navigation) => SetNavigationAction({ navigation })),
            ),
        ),
    );

    initNavigation$ = createEffect(() =>
        this.actions$.pipe(
            ofType(ROOT_EFFECTS_INIT, SetOrgTagAction),
            switchMap(() =>
                combineLatest([
                    this.store.select(isAuthenticatedSelector),
                    this.store.select(authOrgSelector).pipe(distinctUntilChanged()),
                    this.store.select(languageSelector).pipe(distinctUntilChanged()),
                ]).pipe(
                    filter(([auth, authTag, lang]) => !!auth && !!authTag && !!lang),
                    take(1),
                    map(() => GetNavigationAction()),
                ),
            ),
        ),
    );

    @Effect()
    getOrgConfig$ = this.actions$.pipe(
        ofType(GetOrganisationConfigurationAction),
        mergeMap(() =>
            this.store.select(authOrgSelector).pipe(
                filter((tag) => !!tag),
                take(1),
            ),
        ),
        mergeMap((tag) => this.organisationService.getOrganisationConfiguration(tag)),
        map((configuration) => SetOrganisationConfigurationAction({ configuration })),
    );

    @Effect()
    getOrgFeaturesConfig$ = this.actions$.pipe(
        ofType(GetOrganisationFeaturesConfigurationAction),
        mergeMap(() =>
            this.store.select(authOrgSelector).pipe(
                filter((tag) => !!tag),
                take(1),
            ),
        ),
        mergeMap((tag) => this.organisationService.getOrganisationFeaturesConfiguration(tag)),
        map((features) => SetOrganisationFeaturesConfigurationAction({ features })),
    );

    // Services

    @Effect()
    getServices$ = this.actions$.pipe(
        ofType(GetServicesAction),
        switchMap(() =>
            this.serviceStateService.getServices().pipe(
                map((services) => [GetServicesSuccessAction(), SetServicesAction({ services })]),
                catchError((error) => of([GetServicesErrorAction({ error })])),
                switchMap((actions) => actions),
            ),
        ),
    );

    @Effect({ dispatch: false })
    getServicesError$ = this.actions$.pipe(
        ofType(GetServicesErrorAction),
        tap(({ error }) => this.messageService.error(error?.error?.messages?.[0] || error?.message || this.translateService.instant('common.errors.default'))),
    );

    @Effect()
    createService$ = this.actions$.pipe(
        ofType(CreateServiceAction),
        switchMap(({ service }) =>
            this.serviceStateService.createService(service).pipe(
                map((service) => CreateServiceSuccessAction({ service })),
                catchError((error) => of(CreateServiceErrorAction({ error }))),
            ),
        ),
    );

    @Effect()
    createServiceSuccess$ = this.actions$.pipe(
        ofType(CreateServiceSuccessAction),
        tap(({ service }) =>
            this.messageService.success(
                this.translateService.instant('globalMessages.success.service.create', { name: service.name[this.translateService.currentLang] }),
                this.translateService.instant('common.success'),
                { enableHtml: true },
            ),
        ),
        mergeMap(({ service }) =>
            this.store.select(servicesSelector).pipe(
                take(1),
                map((storeServices) => {
                    const newServices = [...storeServices, ...[service]];
                    return [SetServicesAction({ services: newServices })];
                }),
                switchMap((actions) => actions),
            ),
        ),
    );

    @Effect({ dispatch: false })
    createServiceError$ = this.actions$.pipe(
        ofType(CreateServiceErrorAction),
        tap(({ error }) => this.messageService.error(error?.error?.messages?.[0] || error?.error?.message || this.translateService.instant('common.errors.default'))),
    );

    @Effect()
    updateService$ = this.actions$.pipe(
        ofType(UpdateServiceAction),
        mergeMap(({ service }) =>
            this.serviceStateService.updateService(service).pipe(
                map((service) => UpdateServiceSuccessAction({ service })),
                catchError((error) => of(UpdateServiceErrorAction({ error }))),
            ),
        ),
    );

    @Effect()
    updateServiceSuccess$ = this.actions$.pipe(
        ofType(UpdateServiceSuccessAction),
        tap(({ service }) =>
            this.messageService.success(
                this.translateService.instant('globalMessages.success.service.update', { name: service.name[this.translateService.currentLang] }),
                this.translateService.instant('common.success'),
                { enableHtml: true },
            ),
        ),
        mergeMap(({ service }) =>
            this.store.select(servicesSelector).pipe(
                take(1),
                map((storeServices) => {
                    const newServices = storeServices.map((storeService) => (storeService._id === service._id ? service : storeService));
                    return [SetServicesAction({ services: newServices })];
                }),
                switchMap((actions) => actions),
            ),
        ),
    );

    @Effect({ dispatch: false })
    updateServiceError$ = this.actions$.pipe(
        ofType(UpdateServiceErrorAction),
        tap(({ error }) => this.messageService.error(error?.error?.messages?.[0] || error?.error?.message || this.translateService.instant('common.errors.default'))),
    );

    @Effect()
    deleteService$ = this.actions$.pipe(
        ofType(DeleteServiceAction),
        switchMap(({ service }) =>
            this.serviceStateService.deleteService(service._id).pipe(
                map(() => DeleteServiceSuccessAction({ service })),
                catchError((error) => of(DeleteServiceErrorAction({ error }))),
            ),
        ),
    );

    @Effect()
    deleteServiceSuccess$ = this.actions$.pipe(
        ofType(DeleteServiceSuccessAction),
        tap(({ service }) =>
            this.messageService.success(
                this.translateService.instant('globalMessages.success.service.delete', { name: service.name[this.translateService.currentLang] }),
                this.translateService.instant('common.success'),
                { enableHtml: true },
            ),
        ),
        mergeMap(({ service }) =>
            this.store.select(servicesSelector).pipe(
                take(1),
                map((services) => {
                    const newServices = services.filter((storeService) => storeService._id !== service._id);
                    return SetServicesAction({ services: newServices });
                }),
            ),
        ),
    );

    @Effect({ dispatch: false })
    deleteServiceError$ = this.actions$.pipe(
        ofType(DeleteServiceErrorAction),
        tap(({ error }) => this.messageService.error(error?.error?.messages?.[0] || error?.error?.message || this.translateService.instant('common.errors.default'))),
    );

    @Effect()
    duplicateService$ = this.actions$.pipe(
        ofType(DuplicateServiceAction),
        mergeMap(({ service }) =>
            this.serviceStateService.duplicateService(service._id).pipe(
                map((service) => DuplicateServiceSuccessAction({ service })),
                catchError((error) => of(DuplicateServiceErrorAction({ error }))),
            ),
        ),
    );

    @Effect()
    duplicateServiceSuccess$ = this.actions$.pipe(
        ofType(DuplicateServiceSuccessAction),
        tap(({ service }) =>
            this.messageService.success(
                this.translateService.instant('globalMessages.success.service.duplicate', { name: service.name[this.translateService.currentLang] }),
                this.translateService.instant('common.success'),
                { enableHtml: true },
            ),
        ),
        mergeMap(({ service }) =>
            this.store.select(servicesSelector).pipe(
                take(1),
                map((services) =>
                    // const newServices = [...services, ...[service]];
                    SetServicesAction({ services: [...services, ...[service]] }),
                ),
            ),
        ),
    );

    @Effect({ dispatch: false })
    duplicateServiceError$ = this.actions$.pipe(
        ofType(DuplicateServiceErrorAction),
        tap(({ error }) => this.messageService.error(error?.error?.messages?.[0] || error?.message || this.translateService.instant('common.errors.default'))),
    );

    // Service order

    @Effect()
    updateServiceOrder$ = this.actions$.pipe(
        ofType(UpdateServiceOrderAction),
        mergeMap(({ service }) =>
            this.serviceStateService.updateServiceOrder(service).pipe(
                map((services) => UpdateServiceOrderSuccessAction({ changedService: service, services })),
                catchError((error) => of(UpdateServiceOrderErrorAction({ error }))),
            ),
        ),
    );

    @Effect()
    updateServiceOrderSuccess$ = this.actions$.pipe(
        ofType(UpdateServiceOrderSuccessAction),
        tap(({ changedService }) =>
            this.messageService.success(
                this.translateService.instant('globalMessages.success.service.updateOrder', { name: changedService.name[this.translateService.currentLang] }),
                this.translateService.instant('common.success'),
                { enableHtml: true },
            ),
        ),
        map(({ services }) => SetServicesAction({ services })),
    );

    @Effect()
    updateServiceOrderError$ = this.actions$.pipe(
        ofType(UpdateServiceOrderErrorAction),
        tap(({ error }) => this.messageService.error(error?.error?.messages?.[0] || error?.message || this.translateService.instant('common.errors.default'))),
        map(({ error }) => {
            const actions = [];
            if (error?.error?.data) {
                actions.push(SetServicesAction({ services: error.error.data }));
            }
            return actions;
        }),
        switchMap((actions) => actions),
    );

    // Service Categories

    @Effect()
    getServiceCategories$ = this.actions$.pipe(
        ofType(GetServiceCategoriesAction),
        switchMap(() =>
            this.serviceStateService.getServiceCategories().pipe(
                map((categories) => [GetServiceCategoriesSuccessAction(), SetServiceCategoriesAction({ categories })]),
                catchError((error) => of([GetServiceCategoriesErrorAction({ error })])),
                switchMap((actions) => actions),
            ),
        ),
    );

    @Effect({ dispatch: false })
    getServiceCategoriesError$ = this.actions$.pipe(
        ofType(GetServiceCategoriesErrorAction),
        tap(({ error }) => this.messageService.error(error?.error?.messages?.[0] || error?.message || this.translateService.instant('common.errors.default'))),
    );

    @Effect()
    createServiceCategory$ = this.actions$.pipe(
        ofType(CreateServiceCategoryAction),
        mergeMap(({ category }) =>
            this.serviceStateService.createServiceCategory(category).pipe(
                map((category) => [CreateServiceCategorySuccessAction({ category })]),
                catchError((error) => of([CreateServiceCategoryErrorAction({ error })])),
                switchMap((actions) => actions),
            ),
        ),
    );

    @Effect()
    createServiceCategorySuccess$ = this.actions$.pipe(
        ofType(CreateServiceCategorySuccessAction),
        tap(({ category }) =>
            this.messageService.success(
                this.translateService.instant('globalMessages.success.serviceCategory.create', { name: category.name[this.translateService.currentLang] }),
                this.translateService.instant('common.success'),
                { enableHtml: true },
            ),
        ),
        mergeMap(({ category }) =>
            this.store.select(serviceCategoriesSelector).pipe(
                take(1),
                map((services) => SetServiceCategoriesAction({ categories: [...services, ...[category]] })),
            ),
        ),
    );

    @Effect({ dispatch: false })
    createServiceCategoryError$ = this.actions$.pipe(
        ofType(CreateServiceCategoryErrorAction),
        tap(({ error }) => this.messageService.error(error?.error?.messages[0] || this.translateService.instant('common.errors.default'))),
    );

    @Effect()
    updateServiceCategory$ = this.actions$.pipe(
        ofType(UpdateServiceCategoryAction),
        mergeMap(({ category }) =>
            this.serviceStateService.updateServiceCategory(category).pipe(
                map((category) => [UpdateServiceCategorySuccessAction({ category })]),
                catchError((error) => of([UpdateServiceCategoryErrorAction({ error })])),
                switchMap((actions) => actions),
            ),
        ),
    );

    @Effect()
    updateServiceCategorySuccess$ = this.actions$.pipe(
        ofType(UpdateServiceCategorySuccessAction),
        tap(({ category }) =>
            this.messageService.success(
                this.translateService.instant('globalMessages.success.serviceCategory.update', { name: category.name[this.translateService.currentLang] }),
                this.translateService.instant('common.success'),
                { enableHtml: true },
            ),
        ),
        mergeMap(({ category }) =>
            this.store.select(serviceCategoriesSelector).pipe(
                take(1),
                map((categories) => {
                    const newCategories = categories.map((storeCategory) => (storeCategory._id === category._id ? { ...category } : storeCategory));
                    return SetServiceCategoriesAction({ categories: newCategories });
                }),
            ),
        ),
    );

    @Effect({ dispatch: false })
    updateServiceCategoryError$ = this.actions$.pipe(
        ofType(UpdateServiceCategoryErrorAction),
        tap(({ error }) => this.messageService.error(error?.error?.messages[0] || this.translateService.instant('common.errors.default'))),
    );

    @Effect()
    deleteServiceCategory$ = this.actions$.pipe(
        ofType(DeleteServiceCategoryAction),
        mergeMap(({ category }) =>
            this.serviceStateService.deleteServiceCategory(category._id).pipe(
                map(() => DeleteServiceCategorySuccessAction({ category })),
                catchError((error) => of(DeleteServiceCategoryErrorAction({ error }))),
            ),
        ),
    );

    @Effect()
    deleteServiceCategorySuccess$ = this.actions$.pipe(
        ofType(DeleteServiceCategorySuccessAction),
        tap(({ category }) =>
            this.messageService.success(
                this.translateService.instant('globalMessages.success.serviceCategory.delete', { name: category.name[this.translateService.currentLang] }),
                this.translateService.instant('common.success'),
                { enableHtml: true },
            ),
        ),
        mergeMap(({ category }) =>
            this.store.select(serviceCategoriesSelector).pipe(
                take(1),
                map((categories) => {
                    const newCategories = categories.filter((storeCategory) => storeCategory._id !== category._id);
                    return SetServiceCategoriesAction({ categories: newCategories });
                }),
            ),
        ),
    );

    @Effect({ dispatch: false })
    deleteServiceCategoryError$ = this.actions$.pipe(
        ofType(DeleteServiceCategoryErrorAction),
        tap(({ error }) => {
            if (error.status !== 400) {
                this.messageService.error(error?.error?.messages[0] || this.translateService.instant('common.errors.default'));
            }
        }),
    );

    // Service Statuses

    @Effect()
    getServiceStatuses$ = this.actions$.pipe(
        ofType(GetServiceStatusesAction),
        switchMap(() =>
            this.serviceStateService.getServiceStatuses().pipe(
                map((statuses) => [GetServiceStatusesSuccessAction(), SetServiceStatusesAction({ statuses: statuses as ServiceStatusAdminResponse[] })]),
                catchError((error) => of([GetServiceStatusesErrorAction({ error })])),
                switchMap((actions) => actions),
            ),
        ),
    );

    @Effect({ dispatch: false })
    getServiceStatusesError$ = this.actions$.pipe(
        ofType(GetServiceStatusesErrorAction),
        tap(({ error }) => this.messageService.error(error?.error?.messages?.[0] || error?.error?.message || this.translateService.instant('common.errors.default'))),
    );

    @Effect()
    createServiceStatus$ = this.actions$.pipe(
        ofType(CreateServiceStatusAction),
        switchMap(({ status }) =>
            this.serviceStateService.createServiceStatus(status).pipe(
                map((status) => CreateServiceStatusSuccessAction({ status })),
                catchError((error) => of(CreateServiceStatusErrorAction({ error }))),
            ),
        ),
    );

    @Effect()
    createServiceStatusSuccess$ = this.actions$.pipe(
        ofType(CreateServiceStatusSuccessAction),
        tap(({ status }) =>
            this.messageService.success(
                this.translateService.instant('globalMessages.success.serviceStatus.create', { name: status.name[this.translateService.currentLang] }),
                this.translateService.instant('common.success'),
                { enableHtml: true },
            ),
        ),
        mergeMap(({ status }) =>
            this.store.select(serviceStatusesSelector).pipe(
                take(1),
                map((statuses) => SetServiceStatusesAction({ statuses: [...statuses, ...[status]] })),
            ),
        ),
    );

    @Effect({ dispatch: false })
    createServiceStatusError$ = this.actions$.pipe(
        ofType(CreateServiceStatusErrorAction),
        tap(({ error }) => this.messageService.error(error?.error?.messages[0] || this.translateService.instant('common.errors.default'))),
    );

    @Effect()
    updateServiceStatus$ = this.actions$.pipe(
        ofType(UpdateServiceStatusAction),
        switchMap(({ status }) =>
            this.serviceStateService.updateServiceStatus(status).pipe(
                map((status) => UpdateServiceStatusSuccessAction({ status })),
                catchError((error) => of(UpdateServiceStatusErrorAction({ error }))),
            ),
        ),
    );

    @Effect()
    updateServiceStatusSuccess$ = this.actions$.pipe(
        ofType(UpdateServiceStatusSuccessAction),
        tap(({ status }) =>
            this.messageService.success(
                this.translateService.instant('globalMessages.success.serviceStatus.update', { name: status.name[this.translateService.currentLang] }),
                this.translateService.instant('common.success'),
                { enableHtml: true },
            ),
        ),
        mergeMap(({ status }) =>
            this.store.select(serviceStatusesSelector).pipe(
                take(1),
                map((statuses) => {
                    const newStatuses = statuses.map((storeStatus) => (storeStatus._id === status._id ? { ...status } : storeStatus));
                    return SetServiceStatusesAction({ statuses: newStatuses });
                }),
            ),
        ),
    );

    @Effect({ dispatch: false })
    updateServiceStatusError$ = this.actions$.pipe(
        ofType(UpdateServiceStatusErrorAction),
        tap(({ error }) => this.messageService.error(error?.error?.messages?.[0] || error?.error?.message || this.translateService.instant('common.errors.default'))),
    );

    @Effect()
    deleteServiceStatus$ = this.actions$.pipe(
        ofType(DeleteServiceStatusAction),
        switchMap(({ status }) =>
            this.serviceStateService.deleteServiceStatus(status._id).pipe(
                map(() => DeleteServiceStatusSuccessAction({ status })),
                catchError((error) => of(DeleteServiceStatusErrorAction({ error }))),
            ),
        ),
    );

    @Effect()
    deleteServiceStatusSuccess$ = this.actions$.pipe(
        ofType(DeleteServiceStatusSuccessAction),
        tap(({ status }) =>
            this.messageService.success(
                this.translateService.instant('globalMessages.success.serviceStatus.delete', { name: status.name[this.translateService.currentLang] }),
                this.translateService.instant('common.success'),
                { enableHtml: true },
            ),
        ),
        mergeMap(({ status }) =>
            this.store.select(serviceStatusesSelector).pipe(
                take(1),
                map((statuses) => {
                    const newStatuses = statuses.filter((storeStatus) => storeStatus._id !== status._id);
                    return SetServiceStatusesAction({ statuses: newStatuses });
                }),
            ),
        ),
    );

    @Effect({ dispatch: false })
    deleteServiceStatusError$ = this.actions$.pipe(
        ofType(DeleteServiceStatusErrorAction),
        tap(({ error }) => {
            if (error.status !== 400) {
                this.messageService.error(error?.error?.messages?.[0] || error?.error?.message || this.translateService.instant('common.errors.default'));
            }
        }),
    );

    // Collections

    @Effect()
    getCollections$ = this.actions$.pipe(
        ofType(GetCollectionsAction),
        switchMap(() =>
            this.collectionsService.getCollections().pipe(
                map((collections) => [GetCollectionsSuccessAction(), SetCollectionsAction({ collections })]),
                catchError((error) => of([GetCollectionsErrorAction({ error })])),
                switchMap((actions) => actions),
            ),
        ),
    );

    @Effect({ dispatch: false })
    getCollectionsError$ = this.actions$.pipe(
        ofType(GetCollectionsErrorAction),
        tap(({ error }) => this.messageService.error(error?.error?.messages?.[0] || error?.message || this.translateService.instant('common.errors.default'))),
    );

    @Effect()
    createCollection$ = this.actions$.pipe(
        ofType(CreateCollectionAction),
        switchMap(({ collection }) =>
            this.collectionsService.createCollection(collection).pipe(
                map((collection) => CreateCollectionSuccessAction({ collection })),
                catchError((error) => of(CreateCollectionErrorAction({ error }))),
            ),
        ),
    );

    @Effect()
    createCollectionSuccess$ = this.actions$.pipe(
        ofType(CreateCollectionSuccessAction),
        tap(({ collection }) => this.messageService.success(null, this.translateService.instant('common.success'), { enableHtml: true })),
        mergeMap(({ collection }) =>
            this.store.select(collectionsSelector).pipe(
                take(1),
                map((storedCollections) => {
                    const newCollections = [...storedCollections, ...[collection]];
                    return [SetCollectionsAction({ collections: newCollections })];
                }),
                switchMap((actions) => actions),
            ),
        ),
    );

    @Effect({ dispatch: false })
    createCollectionError$ = this.actions$.pipe(
        ofType(CreateCollectionErrorAction),
        tap(({ error }) => this.messageService.error(error?.error?.messages?.[0] || error?.error?.message || this.translateService.instant('common.errors.default'))),
    );

    @Effect()
    updateCollection$ = this.actions$.pipe(
        ofType(UpdateCollectionAction),
        mergeMap(({ collection }) =>
            this.collectionsService.updateCollection(collection).pipe(
                map((collection) => UpdateCollectionSuccessAction({ collection })),
                catchError((error) => of(UpdateCollectionErrorAction({ error }))),
            ),
        ),
    );

    @Effect()
    updateCollectionSuccess$ = this.actions$.pipe(
        ofType(UpdateCollectionSuccessAction),
        tap(({ collection }) => this.messageService.success(null, this.translateService.instant('common.success'), { enableHtml: true })),
        mergeMap(({ collection }) =>
            this.store.select(collectionsSelector).pipe(
                take(1),
                map((storeCollections) => {
                    const newCollections = storeCollections.map((storeCollection) => (storeCollection._id === collection._id ? collection : storeCollection));
                    return [SetCollectionsAction({ collections: newCollections })];
                }),
                switchMap((actions) => actions),
            ),
        ),
    );

    @Effect({ dispatch: false })
    updateCollectionError$ = this.actions$.pipe(
        ofType(UpdateCollectionErrorAction),
        tap(({ error }) => this.messageService.error(error?.error?.messages?.[0] || error?.error?.message || this.translateService.instant('common.errors.default'))),
    );

    @Effect()
    deleteCollection$ = this.actions$.pipe(
        ofType(DeleteCollectionAction),
        switchMap(({ collection }) =>
            this.collectionsService.deleteCollection(collection._id).pipe(
                map(() => DeleteCollectionSuccessAction({ collection })),
                catchError((error) => of(DeleteCollectionErrorAction({ error }))),
            ),
        ),
    );

    @Effect()
    deleteCollectionSuccess$ = this.actions$.pipe(
        ofType(DeleteCollectionSuccessAction),
        tap(({ collection }) => this.messageService.success(null, this.translateService.instant('common.success'), { enableHtml: true })),
        mergeMap(({ collection }) =>
            this.store.select(collectionsSelector).pipe(
                take(1),
                map((collections) => {
                    const newCollections = collections.filter((storeCollection) => storeCollection._id !== collection._id);
                    return SetCollectionsAction({ collections: newCollections });
                }),
            ),
        ),
    );

    @Effect({ dispatch: false })
    deleteCollectionError$ = this.actions$.pipe(
        ofType(DeleteCollectionErrorAction),
        tap(({ error }) => this.messageService.error(error?.error?.messages?.[0] || error?.error?.message || this.translateService.instant('common.errors.default'))),
    );

    @Effect()
    duplicateCollection$ = this.actions$.pipe(
        ofType(DuplicateCollectionAction),
        mergeMap(({ collection }) =>
            this.collectionsService.duplicateCollection(collection._id).pipe(
                map((collection) => DuplicateCollectionSuccessAction({ collection })),
                catchError((error) => of(DuplicateCollectionErrorAction({ error }))),
            ),
        ),
    );

    @Effect()
    duplicateCollectionSuccess$ = this.actions$.pipe(
        ofType(DuplicateCollectionSuccessAction),
        tap(({ collection }) => this.messageService.success(null, this.translateService.instant('common.success'), { enableHtml: true })),
        mergeMap(() =>
            this.store.select(collectionsSelector).pipe(
                take(1),
                map(() => GetCollectionsAction()),
            ),
        ),
    );

    @Effect({ dispatch: false })
    duplicateCollectionError$ = this.actions$.pipe(
        ofType(DuplicateCollectionErrorAction),
        tap(({ error }) => this.messageService.error(error?.error?.messages?.[0] || error?.message || this.translateService.instant('common.errors.default'))),
    );

    // Areas

    @Effect()
    getOrganisationAreas$ = this.actions$.pipe(
        ofType(GetOrganisationAreasAction),
        switchMap(() =>
            this.organisationService.getOrganisationAreasByTag().pipe(
                map((areas) => [GetOrganisationAreasSuccessAction(), SetOrganisationAreasAction({ areas })]),
                catchError((error) => of([GetOrganisationAreasErrorAction({ error })])),
                switchMap((actions) => actions),
            ),
        ),
    );

    @Effect({ dispatch: false })
    getOrganisationsAreasError = this.actions$.pipe(
        ofType(GetOrganisationAreasErrorAction),
        tap(({ error }) => this.messageService.error(error?.error?.messages?.[0] || error?.error?.message || this.translateService.instant('common.errors.default'))),
    );

    @Effect()
    createOrganisationArea$ = this.actions$.pipe(
        ofType(CreateOrganisationAreaAction),
        switchMap(({ area }) =>
            this.organisationService.createOrganisationArea(area).pipe(
                map((area) => [CreateOrganisationAreaSuccessAction({ area })]),
                catchError((error) => of([CreateOrganisationAreaErrorAction({ error })])),
                switchMap((actions) => actions),
            ),
        ),
    );

    @Effect()
    createOrganisationAreaSuccess$ = this.actions$.pipe(
        ofType(CreateOrganisationAreaSuccessAction),
        tap(({ area }) => this.messageService.success(null, this.translateService.instant('common.success'), { enableHtml: true })),
        mergeMap(({ area }) =>
            this.store.select(areasSelector).pipe(
                take(1),
                map((storeAreas) => {
                    const newAreas = [...storeAreas, area];
                    return SetOrganisationAreasAction({ areas: newAreas });
                }),
            ),
        ),
    );

    @Effect({ dispatch: false })
    createOrganisationAreaError$ = this.actions$.pipe(
        ofType(CreateOrganisationAreaErrorAction),
        tap(({ error }) => this.messageService.error(error?.error?.messages?.[0] || error?.error?.message || this.translateService.instant('common.errors.default'))),
    );

    @Effect()
    updateOrganisationArea$ = this.actions$.pipe(
        ofType(UpdateOrganisationAreaAction),
        switchMap(({ area }) =>
            this.organisationService.updateOrganisationArea(area).pipe(
                map((area) => [UpdateOrganisationAreaSuccessAction({ area })]),
                catchError((error) => of([UpdateOrganisationAreaErrorAction({ error })])),
                switchMap((actions) => actions),
            ),
        ),
    );

    @Effect()
    updateOrganisationAreaSuccess$ = this.actions$.pipe(
        ofType(UpdateOrganisationAreaSuccessAction),
        tap(({ area }) => this.messageService.success(null, this.translateService.instant('common.success'), { enableHtml: true })),
        mergeMap(({ area }) =>
            this.store.select(areasSelector).pipe(
                take(1),
                map((areas) => {
                    const newAreas = areas.map((storeArea) => (storeArea._id === area._id ? { ...area } : storeArea));
                    return SetOrganisationAreasAction({ areas: newAreas });
                }),
            ),
        ),
    );

    @Effect()
    deleteOrganisationArea$ = this.actions$.pipe(
        ofType(DeleteOrganisationAreaAction),
        switchMap(({ area }) =>
            this.organisationService.deleteOrganisationArea(area._id).pipe(
                map((response) => [DeleteOrganisationAreaSuccessAction({ area })]),
                catchError((error) => of([DeleteOrganisationAreaErrorAction({ error })])),
                switchMap((actions) => actions),
            ),
        ),
    );

    @Effect()
    deleteOrganisationAreaSuccess$ = this.actions$.pipe(
        ofType(DeleteOrganisationAreaSuccessAction),
        tap(({ area }) => this.messageService.success(null, this.translateService.instant('common.success'), { enableHtml: true })),
        mergeMap(({ area }) =>
            this.store.select(areasSelector).pipe(
                take(1),
                map((storeAreas) => {
                    const newsAreas = storeAreas.filter((storeArea) => storeArea._id !== area._id);
                    return SetOrganisationAreasAction({ areas: newsAreas });
                }),
            ),
        ),
    );

    @Effect()
    setOrganisationColour$ = this.actions$.pipe(
        ofType(SetOrganisationColourAction),
        switchMap(({ orgTag, colour }) =>
            this.organisationService.setOrganisationColour(orgTag, colour).pipe(
                // Map the response to a success action
                map(() => SetOrganisationColourSuccessAction({ orgTag, colour })),
                // Handle errors
                catchError((error) => of(SetOrganisationColourFailureAction({ error }))),
            ),
        ),
    );

    @Effect()
    getOrganisationColour$ = this.actions$.pipe(
        ofType(GetOrganisationColourAction),
        switchMap(({}) =>
            this.organisationService.getOrganisationColour().pipe(
                map((colour) => GetOrganisationColourSuccessAction({ colour })),
                catchError((error) => of(GetOrganisationColourFailureAction({ error }))),
            ),
        ),
    );

    @Effect()
    clearOrganisationAreas$ = this.actions$.pipe(
        ofType(ClearOrganisationAreasAction),
        switchMap(() => [SetOrganisationAreasAction({ areas: [] })]),
    );

    @Effect()
    getRoles$ = this.actions$.pipe(
        ofType(GetAccessAction),
        switchMap(() =>
            this.accessService.getRoles().pipe(
                map((access) => [GetAccessSuccessAction(), SetAccessAction({ access })]),
                catchError((error) => of([GetAccessErrorAction({ error })])),
                switchMap((actions) => actions),
            ),
        ),
    );

    @Effect({ dispatch: false })
    getRolesError$ = this.actions$.pipe(
        ofType(GetAccessErrorAction),
        tap(({ error }) => console.error(error)),
    );

    @Effect()
    createRole$ = this.actions$.pipe(
        ofType(CreateAccessAction),
        switchMap(({ access }) =>
            this.accessService.createRole(access).pipe(
                map((access) => CreateAccessSuccessAction({ access })),
                catchError((error) => of(CreateAccessErrorAction({ error }))),
            ),
        ),
    );

    @Effect()
    createRoleSuccess$ = this.actions$.pipe(
        ofType(CreateAccessSuccessAction),
        tap(({ access }) => this.messageService.success(null, this.translateService.instant('common.success'), { enableHtml: true })),
        mergeMap(({ access }) =>
            this.store.select(accessSelector).pipe(
                take(1),
                map((storedAccesses) => {
                    const newAccesses = [...storedAccesses, ...[access]];
                    return [SetAccessAction({ access: newAccesses })];
                }),
                switchMap((actions) => actions),
            ),
        ),
    );

    @Effect({ dispatch: false })
    createRoleError$ = this.actions$.pipe(
        ofType(CreateAccessErrorAction),
        tap(({ error }) => this.messageService.error(error?.error?.messages?.[0] || error?.error?.message || this.translateService.instant('common.errors.default'))),
    );

    @Effect()
    updateRole$ = this.actions$.pipe(
        ofType(UpdateAccessAction),
        mergeMap(({ access, roleId }) =>
            this.accessService.updateRole(access, roleId).pipe(
                map((access) => UpdateAccessSuccessAction({ access })),
                catchError((error) => of(UpdateAccessErrorAction({ error }))),
            ),
        ),
    );

    @Effect()
    updateRoleSuccess$ = this.actions$.pipe(
        ofType(UpdateAccessSuccessAction),
        tap(({ access }) => this.messageService.success(null, this.translateService.instant('common.success'), { enableHtml: true })),
        mergeMap(({ access }) =>
            this.store.select(accessSelector).pipe(
                take(1),
                map((storeAccesses) => {
                    const newAccess = storeAccesses.map((storeAccess) => (storeAccess._id === access._id ? access : storeAccess));
                    return [SetAccessAction({ access: newAccess })];
                }),
                switchMap((actions) => actions),
            ),
        ),
    );

    @Effect({ dispatch: false })
    updateRoleError$ = this.actions$.pipe(
        ofType(UpdateAccessErrorAction),
        tap(({ error }) => this.messageService.error(error?.error?.messages?.[0] || error?.error?.message || this.translateService.instant('common.errors.default'))),
    );

    @Effect()
    deleteAccess$ = this.actions$.pipe(
        ofType(DeleteAccessAction),
        switchMap(({ access }) =>
            this.accessService.deleteAccess(access._id).pipe(
                map(() => DeleteAccessSuccessAction({ access })),
                catchError((error) => of(DeleteAccessErrorAction({ error }))),
            ),
        ),
    );

    @Effect()
    deleteAccessSuccess$ = this.actions$.pipe(
        ofType(DeleteAccessSuccessAction),
        tap(({ access }) => this.messageService.success(null, this.translateService.instant('common.success'), { enableHtml: true })),
        mergeMap(({ access }) =>
            this.store.select(accessSelector).pipe(
                take(1),
                map((accesses) => {
                    const newAccess = accesses.filter((storeAccess) => storeAccess._id !== access._id);
                    return SetAccessAction({ access: newAccess });
                }),
            ),
        ),
    );

    @Effect({ dispatch: false })
    deleteAccessError$ = this.actions$.pipe(
        ofType(DeleteAccessErrorAction),
        tap(({ error }) => this.messageService.error(error?.error?.messages?.[0] || error?.error?.message || this.translateService.instant('common.errors.default'))),
    );

    @Effect()
    BatchAssignRole$ = this.actions$.pipe(
        ofType(BatchAssignRoleAction),
        switchMap(({ assignments }) =>
            this.accessService.assignRoles({ assignments }).pipe(
                map((response) => {
                    const successfulAssignments = response.successful.map((item) => ({
                        userId: item.userId,
                        roleId: item.roleId,
                    }));
                    return BatchAssignRoleSuccessAction({ assignments: successfulAssignments });
                }),
                catchError((error) => of(BatchAssignRoleErrorAction({ error }))),
            ),
        ),
    );

    @Effect({ dispatch: false })
    BatchAssignRoleSuccess$ = this.actions$.pipe(
        ofType(BatchAssignRoleSuccessAction),
        tap(({ assignments }) => this.messageService.success(null, this.translateService.instant('common.success'), { enableHtml: true })),
    );

    @Effect({ dispatch: false })
    BatchAssignRoleError$ = this.actions$.pipe(
        ofType(BatchAssignRoleErrorAction),
        tap(({ error }) => this.messageService.error(error?.error?.messages?.[0] || error?.error?.message || this.translateService.instant('common.errors.default'))),
    );

    @Effect()
    AssignRole$ = this.actions$.pipe(
        ofType(AssignRoleAction),
        switchMap(({ roleId, userid }) =>
            this.accessService.assignRole(roleId, userid).pipe(
                map((user) => AssignRoleSuccessAction()),
                catchError((error) => of(AssignRoleErrorAction({ error }))),
            ),
        ),
    );
}
